/** Route stubs: the areas other increments deliver render the `unavailable` state (INC-0608 scope). */
import { AREA_LABELS, hashOf, type Area } from "../app/router.ts";
import { h } from "../app/dom.ts";
import { renderStateBanner } from "../app/state.ts";
import type { ViewContext, ViewHandle } from "../app/view.ts";
import { handleOf, makeFrame } from "./frame.ts";

/** Which increment delivers each area (the wave-3 specification). */
export const DELIVERED_BY: Readonly<Record<Exclude<Area, "today">, string>> = {
  opportunities: "INC-0608",
  watchlists: "INC-0610",
  portfolio: "INC-0609",
  orders: "INC-0609",
  research: "INC-0610",
  data: "INC-0610",
  operations: "INC-0610",
  security: "INC-0610",
  settings: "INC-0611",
};

/** Areas that hold P0-E content: gated by the server's `routing_enabled` capability (ruling 0.14). */
export const GATED_UNTIL_G5: readonly Area[] = ["orders", "portfolio"];

/** Render an area that has no view yet as `unavailable`, or as a G5 blocker when routing is not enabled. */
export function mountStub(ctx: ViewContext, area: Exclude<Area, "today">): ViewHandle {
  const frame = makeFrame(`stub-${area}`, AREA_LABELS[area]);
  const gated = GATED_UNTIL_G5.includes(area) && !ctx.capabilities.routingEnabled;
  frame.banner.append(
    renderStateBanner({
      kind: "unavailable",
      reason: gated
        ? "this area is not enabled before G5 (broker routing is off); the server's capability flag is false"
        : `this area is delivered by ${DELIVERED_BY[area]} and is not available yet`,
    }),
  );
  return handleOf(frame, ctx);
}

/** The Opportunities landing page: links to the scanner, the board and Track B. */
export function mountOpportunities(ctx: ViewContext): ViewHandle {
  const frame = makeFrame("opportunities", "Opportunities");
  frame.body.append(
    h(
      "ul",
      {},
      h(
        "li",
        {},
        h("a", { attrs: { href: hashOf("opportunities", "scanner") } }, "Universe and scanner"),
      ),
      h(
        "li",
        {},
        h("a", { attrs: { href: hashOf("opportunities", "board") } }, "Recommendation board"),
      ),
      h(
        "li",
        {},
        h(
          "a",
          { attrs: { href: hashOf("opportunities", "trackb") } },
          "Track B recommendations (read-only)",
        ),
      ),
    ),
  );
  return handleOf(frame, ctx);
}

/** An unknown route fails closed with an error state. */
export function mountNotFound(ctx: ViewContext): ViewHandle {
  const frame = makeFrame("not-found", "Page not found");
  frame.banner.append(
    renderStateBanner({ kind: "error", correlationId: null, message: "no such page" }),
  );
  return handleOf(frame, ctx);
}
